import { z } from 'zod'

export const publicStatsResponseSchema = z.object({
  users: z.number().int().nonnegative(),
  businesses: z.number().int().nonnegative(),
  listings: z.number().int().nonnegative(),
  /** Current or future event instances (each future occurrence counted). */
  upcomingEvents: z.number().int().nonnegative(),
})

export const businessMetricsResponseSchema = z.object({
  totalEventInterests: z.number().int().nonnegative(),
  totalListingInterests: z.number().int().nonnegative(),
  totalInterests: z.number().int().nonnegative(),
  publishedEventsCount: z.number().int().nonnegative(),
  publishedListingsCount: z.number().int().nonnegative(),
})

/**
 * A business's start page numbers, across its own places (listings) and events. People are only
 * ever counted, never listed.
 */
export const businessOverviewResponseSchema = z.object({
  /** When the snapshot was taken. */
  generatedAt: z.string().datetime(),
  /** Today's date in UTC, the calendar the place and event analytics count in. */
  today: z.string().date(),
  /** Distinct people following the business or one of its places. */
  followers: z.number().int().nonnegative(),
  /**
   * Distinct people going, maybe or en route in upcoming plans linked to one of the business's
   * places or events, or to a spot at one of its places.
   */
  planningToVisit: z.number().int().nonnegative(),
  /** People present right now at a spot at one of the business's places. */
  hereNow: z.number().int().nonnegative(),
  /** How often the places and events were shown in feeds and search. */
  impressions: z.object({
    last30d: z.number().int().nonnegative(),
    previous30d: z.number().int().nonnegative(),
  }),
  /** How often their detail pages were opened. */
  views: z.object({
    last30d: z.number().int().nonnegative(),
    previous30d: z.number().int().nonnegative(),
  }),
  /** The last 30 days, oldest first, ending today. */
  daily: z.array(
    z.object({
      day: z.string().date(),
      impressions: z.number().int().nonnegative(),
      views: z.number().int().nonnegative(),
    }),
  ),
})

/** Requestable granularity. 'all' lets the server pick the bucket size from the data span. */
export const analyticsGranularitySchema = z.enum(['day', 'week', 'month', 'all'])

/** Concrete bucket size the server actually used (never 'all'). */
export const resolvedGranularitySchema = z.enum(['day', 'week', 'month'])

const count = () => z.number().int().nonnegative()

/** One time bucket of a listing's engagement (bucket = ISO date of the bucket start). */
export const listingAnalyticsBucketSchema = z.object({
  bucket: z.string(),
  views: count(),
  interactions: count(),
  follows: count(),
  impressions: count(),
})

export const listingAnalyticsResponseSchema = z.object({
  granularity: resolvedGranularitySchema,
  totals: z.object({
    views: count(),
    interactions: count(),
    follows: count(),
    impressions: count(),
  }),
  series: z.array(listingAnalyticsBucketSchema),
})

/** One time bucket of an event's engagement (bucket = ISO date of the bucket start). */
export const eventAnalyticsBucketSchema = z.object({
  bucket: z.string(),
  views: count(),
  interactions: count(),
  likes: count(),
  impressions: count(),
  checkIns: count(),
})

export const eventAnalyticsResponseSchema = z.object({
  granularity: resolvedGranularitySchema,
  totals: z.object({
    views: count(),
    interactions: count(),
    likes: count(),
    impressions: count(),
    checkIns: count(),
  }),
  series: z.array(eventAnalyticsBucketSchema),
})

/** One day×tool×client bucket of MCP tool-call volume. */
export const mcpUsageRowSchema = z.object({
  day: z.string(),
  tool: z.string(),
  client: z.string(),
  calls: count(),
})

export const mcpUsageResponseSchema = z.object({
  days: z.number().int().positive(),
  totalCalls: count(),
  rows: z.array(mcpUsageRowSchema),
})

export type PublicStatsResponse = z.infer<typeof publicStatsResponseSchema>
export type BusinessMetricsResponse = z.infer<typeof businessMetricsResponseSchema>
export type BusinessOverviewResponse = z.infer<typeof businessOverviewResponseSchema>
export type AnalyticsGranularity = z.infer<typeof analyticsGranularitySchema>
export type ResolvedGranularity = z.infer<typeof resolvedGranularitySchema>
export type ListingAnalyticsResponse = z.infer<typeof listingAnalyticsResponseSchema>
export type EventAnalyticsResponse = z.infer<typeof eventAnalyticsResponseSchema>
export type McpUsageResponse = z.infer<typeof mcpUsageResponseSchema>
